// Scan management API
import { api, API_BASE } from './client';

export interface CreateScanRequest {
  name: string;
  description?: string;
  config: any;
  target_ids: number[];
  // Phase-1 reuse: skip the port-scan phase and reuse a previous scan's phase-1
  // (port-scan) results from the same project. Targets are inherited from it.
  reuse_phase1_from_scan_id?: number | null;
}

export interface ScanControlResponse {
  message: string;
  status: string;
  scan_id: number;
}

export interface ScanStatus {
  currentTarget: string;
  currentEngine: string;
  targetsTotal: number;
  targetsComplete: number;
  engineStatus: Record<string, string>;
  isPaused: boolean;
  startTime: string;
  lastUpdate: string;
}

// Enhanced Scans API with pause/resume functionality
export const scansAPI = {
  getAll: (params?: { page?: number; limit?: number; status?: string; project_name?: string; scan_type?: string; search?: string }) => {
    console.log('🔍 Fetching all scans across all projects');
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.project_name) searchParams.append('project_name', params.project_name);
    if (params?.scan_type) searchParams.append('scan_type', params.scan_type);
    if (params?.search) searchParams.append('search', params.search);
    const queryString = searchParams.toString();
    return api.get(`/scans${queryString ? `?${queryString}` : ''}`);
  },
  getByProject: (projectId: number, params?: { page?: number; limit?: number; status?: string }) => {
    console.log('🔍 Fetching scans for project:', projectId);
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    const queryString = searchParams.toString();
    return api.get(`/project/${projectId}/scans${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => {
    console.log('🔍 Fetching scan:', id);
    return api.get(`/scans/${id}`);
  },
  getStatus: (id: number) => {
    console.log('📊 Fetching live scan status:', id);
    return api.get<{ status: ScanStatus; is_active: boolean }>(`/scans/${id}/status`);
  },
  create: (projectId: number, data: CreateScanRequest) => {
    console.log('=== SCAN CREATION DEBUG ===');
    console.log('📊 Creating scan for project ID:', projectId);
    console.log('📊 Project ID type:', typeof projectId);
    console.log('📊 Is project ID valid number?', !isNaN(projectId) && projectId > 0);
    console.log('📊 Scan data:', JSON.stringify(data, null, 2));
    console.log('📊 Target IDs:', data.target_ids);
    console.log('📊 Target IDs length:', data.target_ids?.length);
    console.log('📊 Full URL will be:', `${API_BASE}/project/${projectId}/scans`);

    // Validate inputs before making request
    if (!projectId || isNaN(projectId) || projectId <= 0) {
      console.error('❌ Invalid project ID provided:', projectId);
      return Promise.reject(new Error(`Invalid project ID: ${projectId}`));
    }

    if (!data.name || !data.name.trim()) {
      console.error('❌ Scan name is required');
      return Promise.reject(new Error('Scan name is required'));
    }

    if (!data.target_ids || data.target_ids.length === 0) {
      console.error('❌ At least one target must be selected');
      return Promise.reject(new Error('At least one target must be selected'));
    }

    if (!data.config) {
      console.error('❌ Scan configuration is required');
      return Promise.reject(new Error('Scan configuration is required'));
    }

    console.log('✅ All validations passed, making API request...');
    return api.post(`/project/${projectId}/scans`, data);
  },
  update: (id: number, data: any) => {
    console.log('📝 Updating scan:', id, data);
    return api.put(`/scans/${id}`, data);
  },
  delete: (id: number) => {
    console.log('🗑️ Deleting scan:', id);
    return api.delete(`/scans/${id}`);
  },
  bulkDelete: (scanIds: number[]) => {
    console.log('🗑️ Bulk deleting scans:', scanIds);
    return api.post('/scans/bulk-delete', { scan_ids: scanIds });
  },
  // Enhanced scan control methods
  start: (id: number) => {
    console.log('▶️ Starting scan:', id);
    return api.post<ScanControlResponse>(`/scans/${id}/start`);
  },
  // Clone a scan into a NEW pending scan with identical config + targets (does NOT
  // touch the original scan's results). Returns { scan: {...new scan...} }.
  clone: (id: number) => {
    console.log('🔁 Cloning scan for rescan:', id);
    return api.post<{ scan: { id: number } }>(`/scans/${id}/clone`);
  },
  // Re-capture ONLY the failed screenshots (cert errors etc.) of a completed scan.
  // Screenshot engine only — no nmap/nuclei/etc. re-run on third-party hosts.
  rescreenshotFailed: (id: number) => {
    console.log('📸 Re-capturing failed screenshots for scan:', id);
    return api.post<{ total: number; recaptured: number; still_failed: number; capped: boolean; message: string }>(
      `/scans/${id}/rescreenshot-failed`
    );
  },
  pause: (id: number) => {
    console.log('⏸️ Pausing scan:', id);
    return api.post<ScanControlResponse>(`/scans/${id}/pause`);
  },
  resume: (id: number) => {
    console.log('▶️ Resuming scan:', id);
    return api.post<ScanControlResponse>(`/scans/${id}/resume`);
  },
  stop: (id: number) => {
    console.log('⏹️ Stopping scan:', id);
    return api.post<ScanControlResponse>(`/scans/${id}/stop`);
  },
  getResults: (id: number, includeFalsePositives: boolean = true, language?: string, includeSuppressed: boolean = true) => {
    console.log('📊 Fetching results for scan:', id, 'includeFalsePositives:', includeFalsePositives, 'language:', language, 'includeSuppressed:', includeSuppressed);
    const params = new URLSearchParams();
    if (includeFalsePositives) params.append('include_false_positives', 'true');
    if (includeSuppressed) params.append('include_suppressed', 'true');
    if (language && language !== 'en') params.append('language', language);
    const queryString = params.toString();
    return api.get(`/scans/${id}/results${queryString ? '?' + queryString : ''}`, {
      timeout: 120000 // 2 minutes timeout for large result sets
    });
  },
  // Get scan results grouped by IP address (for IP-centric view with hostnames as subtitles)
  getResultsGrouped: (id: number, includeFalsePositives: boolean = true, language?: string, includeSuppressed: boolean = true) => {
    console.log('📊 Fetching grouped results for scan:', id);
    const params = new URLSearchParams();
    if (includeFalsePositives) params.append('include_false_positives', 'true');
    if (includeSuppressed) params.append('include_suppressed', 'true');
    if (language && language !== 'en') params.append('language', language);
    const queryString = params.toString();
    return api.get(`/scans/${id}/results/grouped${queryString ? '?' + queryString : ''}`, {
      timeout: 120000 // 2 minutes timeout for large result sets
    });
  },
  exportHTML: (id: number) => {
    console.log('📄 Exporting HTML report for scan:', id);
    return api.get(`/scans/${id}/export/html`, { responseType: 'blob' });
  },
  exportPDF: (id: number) => {
    console.log('📄 Exporting PDF report for scan:', id);
    return api.get(`/scans/${id}/export/pdf`, { responseType: 'blob' });
  },
  exportEnhancedHTML: (id: number) => {
    console.log('📊 Exporting enhanced HTML report with charts for scan:', id);
    return api.get(`/scans/${id}/export/enhanced-html`, {
      responseType: 'blob',
      timeout: 60000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
  exportScanReport: (id: number) => {
    console.log('📋 Exporting traditional scan report for scan:', id);
    return api.get(`/scans/${id}/export/scan-report`, {
      responseType: 'blob',
      timeout: 30000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
  exportJSON: (id: number) => {
    console.log('📋 Exporting JSON data for scan:', id);
    return api.get(`/scans/${id}/export/json`, { responseType: 'blob' });
  },
  exportOpenJSON: (id: number, options?: { language?: string; source?: string; includeAll?: boolean }) => {
    console.log('📋 Exporting OpenJSON (PentestApp) data for scan:', id, options);
    const params = new URLSearchParams();
    if (options?.language) params.append('language', options.language);
    if (options?.source) params.append('source', options.source);
    if (options?.includeAll) params.append('include_all', 'true');
    const queryString = params.toString();
    return api.get(`/scans/${id}/export/openjson${queryString ? '?' + queryString : ''}`, { responseType: 'blob' });
  },
  exportCSV: (id: number, options?: { severity?: string; excludeFP?: boolean; status?: string; includeSuppressed?: boolean; language?: string }) => {
    console.log('📋 Exporting CSV data for scan:', id, options);
    const params = new URLSearchParams();
    if (options?.severity) params.append('severity', options.severity);
    if (options?.excludeFP) params.append('exclude_fp', 'true');
    if (options?.status) params.append('remediation_status', options.status);
    if (options?.includeSuppressed) params.append('include_suppressed', 'true');
    if (options?.language) params.append('language', options.language);
    const queryString = params.toString();
    return api.get(`/scans/${id}/export/csv${queryString ? '?' + queryString : ''}`, { responseType: 'blob' });
  },
  exportExcel: (id: number, options?: { severity?: string; excludeFP?: boolean; status?: string; includeSuppressed?: boolean; language?: string }) => {
    console.log('📊 Exporting Excel data for scan:', id, options);
    const params = new URLSearchParams();
    if (options?.severity) params.append('severity', options.severity);
    if (options?.excludeFP) params.append('exclude_fp', 'true');
    if (options?.status) params.append('remediation_status', options.status);
    if (options?.includeSuppressed) params.append('include_suppressed', 'true');
    if (options?.language) params.append('language', options.language);
    const queryString = params.toString();
    return api.get(`/scans/${id}/export/excel${queryString ? '?' + queryString : ''}`, { responseType: 'blob' });
  },
  getRemediationStats: (id: number) => {
    console.log('📊 Fetching remediation stats for scan:', id);
    return api.get(`/scans/${id}/remediation-stats`);
  },
  exportTechnologyReport: (id: number) => {
    console.log('🔧 Exporting technology report for scan:', id);
    return api.get(`/scans/${id}/export/technology-report`, {
      responseType: 'blob',
      timeout: 180000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
  getTechnologyReportData: (id: number) => {
    console.log('🔧 Fetching technology report data for scan:', id);
    console.log('🔧 API Base URL:', API_BASE);
    console.log('🔧 Full URL will be:', `${API_BASE}/scans/${id}/technology-report-data`);

    // Check if we have a valid auth token
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.error('🔧 ERROR: No auth token found in localStorage');
      return Promise.reject(new Error('Authentication required - please log in again'));
    }

    return api.get(`/scans/${id}/technology-report-data`, {
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      timeout: 180000
    }).catch(error => {
      console.error('🔧 Technology report API error:', error);
      if (error.response?.status === 401) {
        console.error('🔧 Authentication failed - token may be expired');
        localStorage.removeItem('auth_token');
        throw new Error('Authentication expired - please log in again');
      } else if (error.response?.status === 404) {
        throw new Error('Scan not found or technology report not available');
      } else if (error.response?.status === 500) {
        throw new Error('Server error generating technology report - please try again');
      }
      throw error;
    });
  },
  exportTechnologyReportPDF: (id: number) => {
    console.log('📄 Exporting technology report PDF for scan:', id);
    return api.get(`/scans/${id}/export/technology-report-pdf`, { responseType: 'blob' });
  },
  // HTML-to-PDF exports (uses headless Chrome for consistent styling)
  exportEnhancedPDF: (id: number) => {
    console.log('Exporting enhanced HTML as PDF for scan:', id);
    return api.get(`/scans/${id}/export/enhanced-pdf`, { timeout: 180000 });
  },
  exportScanReportPDF: (id: number) => {
    console.log('Exporting scan report as PDF for scan:', id);
    return api.get(`/scans/${id}/export/scan-report-pdf`, { timeout: 180000 });
  },
  exportBasicPDF: (id: number) => {
    console.log('Exporting basic HTML as PDF for scan:', id);
    return api.get(`/scans/${id}/export/basic-pdf`, { timeout: 180000 });
  },
  exportTechnologyReportPDFChrome: (id: number) => {
    console.log('Exporting technology report as PDF (Chrome) for scan:', id);
    return api.get(`/scans/${id}/export/technology-report-pdf-chrome`, { timeout: 180000 });
  },
  // DOCX exports (HTML-to-DOCX via pandoc)
  exportEnhancedDocx: (id: number) => {
    return api.get(`/scans/${id}/export/enhanced-docx`, { timeout: 180000 });
  },
  exportScanReportDocx: (id: number) => {
    return api.get(`/scans/${id}/export/scan-report-docx`, { timeout: 180000 });
  },
  exportBasicDocx: (id: number) => {
    return api.get(`/scans/${id}/export/basic-docx`, { timeout: 180000 });
  },
  exportTechnologyReportDocx: (id: number) => {
    return api.get(`/scans/${id}/export/technology-report-docx`, { timeout: 180000 });
  },
  exportScreenshotGallery: (id: number) => {
    console.log('📸 Exporting screenshot gallery for scan:', id);
    return api.get(`/scans/${id}/export/screenshot-gallery`, {
      responseType: 'blob',
      timeout: 180000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
  // Full Assessment Report (passive+active combined)
  exportFullAssessmentReport: (projectId: number, format: 'html' | 'json' = 'html', language?: string) => {
    console.log('📋 Exporting full assessment report for project:', projectId);
    const params = new URLSearchParams();
    if (format === 'json') params.append('format', 'json');
    if (language) params.append('language', language);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    if (format === 'json') {
      return api.get(`/projects/${projectId}/export/full-assessment${queryString}`);
    }
    return api.get(`/projects/${projectId}/export/full-assessment${queryString}`, {
      responseType: 'blob',
      timeout: 180000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
  // Progress details preference management
  getProgressDetailsPreference: () => {
    console.log('⚙️ Fetching progress details preference');
    return api.get('/user/progress-details-preference');
  },
  updateProgressDetailsPreference: (enabled: boolean) => {
    console.log('💾 Updating progress details preference:', enabled);
    return api.put('/user/progress-details-preference', { enabled });
  },
  // Console output retrieval
  getScanConsoleOutput: (id: number) => {
    console.log('📟 Fetching console output for scan:', id);
    return api.get(`/scans/${id}/console`);
  },
  // Graphviz network topology
  getGraphviz: (id: number) => {
    console.log('🕸️ Fetching Graphviz network topology for scan:', id);
    return api.get(`/scans/${id}/graphviz`);
  },
  // Target deletion from scan
  deleteTarget: (scanId: number, targetId: number) => {
    console.log('🗑️ Deleting target from scan:', { scanId, targetId });
    return api.delete(`/scans/${scanId}/targets/${targetId}`);
  },
  // False positive management
  markFalsePositive: (scanId: number, resultId: number, reason: string = '') => {
    console.log('🚩 Marking result as false positive:', { scanId, resultId, reason });
    return api.post(`/scans/${scanId}/results/${resultId}/false-positive`, { reason });
  },
  unmarkFalsePositive: (scanId: number, resultId: number) => {
    console.log('✅ Unmarking false positive:', { scanId, resultId });
    return api.delete(`/scans/${scanId}/results/${resultId}/false-positive`);
  },
  getFalsePositives: (scanId: number) => {
    console.log('📋 Fetching false positives for scan:', scanId);
    return api.get(`/scans/${scanId}/false-positives`);
  },
  bulkMarkFalsePositive: (scanId: number, resultIds: number[], reason: string = '') => {
    console.log('🚩 Bulk marking results as false positive:', { scanId, resultIds, reason });
    return api.post(`/scans/${scanId}/results/bulk-false-positive`, { result_ids: resultIds, reason });
  },
  bulkUnmarkFalsePositive: (scanId: number, resultIds: number[]) => {
    console.log('✅ Bulk unmarking false positives:', { scanId, resultIds });
    return api.delete(`/scans/${scanId}/results/bulk-false-positive`, { data: { result_ids: resultIds } });
  },
  // Soft-delete / Trashbin management
  softDeleteResult: (scanId: number, resultId: number, reason: string = '') => {
    console.log('🗑️ Soft-deleting result:', { scanId, resultId, reason });
    return api.post(`/scans/${scanId}/results/${resultId}/delete`, { reason });
  },
  restoreResult: (scanId: number, resultId: number) => {
    console.log('♻️ Restoring result:', { scanId, resultId });
    return api.post(`/scans/${scanId}/results/${resultId}/restore`);
  },
  getDeletedResults: (scanId: number) => {
    console.log('🗑️ Fetching deleted results:', { scanId });
    return api.get(`/scans/${scanId}/deleted-results`);
  },
  bulkSoftDelete: (scanId: number, resultIds: number[], reason: string = '') => {
    console.log('🗑️ Bulk soft-deleting results:', { scanId, resultIds, reason });
    return api.post(`/scans/${scanId}/results/bulk-delete`, { result_ids: resultIds, reason });
  },
  bulkRestore: (scanId: number, resultIds: number[]) => {
    console.log('♻️ Bulk restoring results:', { scanId, resultIds });
    return api.post(`/scans/${scanId}/results/bulk-restore`, { result_ids: resultIds });
  },
  permanentDeleteResult: (scanId: number, resultId: number) => {
    console.log('💀 Permanently deleting result:', { scanId, resultId });
    return api.delete(`/scans/${scanId}/results/${resultId}/permanent`);
  },
  // Bulk Remediation Status
  bulkUpdateRemediation: (resultIds: number[], status: string, notes?: string, assignee?: string, dueDateDays?: number) => {
    console.log('🔧 Bulk updating remediation status:', { resultIds, status, notes, assignee, dueDateDays });
    return api.post('/results/bulk-remediation', {
      result_ids: resultIds,
      status,
      notes,
      assignee,
      due_date_days: dueDateDays
    });
  },
  // Severity Trend
  getSeverityTrend: (projectId?: number, days?: number) => {
    console.log('📈 Fetching severity trend:', { projectId, days });
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId.toString());
    if (days) params.append('days', days.toString());
    const queryString = params.toString();
    return api.get(`/severity-trend${queryString ? '?' + queryString : ''}`);
  },
  // Timing Statistics API methods
  getTimingStatistics: (id: number) => {
    console.log('📊 Fetching timing statistics for scan:', id);
    return api.get(`/scans/${id}/timing-statistics`);
  },
  getEnginePerformance: (id: number) => {
    console.log('⚡ Fetching engine performance for scan:', id);
    return api.get(`/scans/${id}/engine-performance`);
  },
  getTargetTimings: (id: number) => {
    console.log('🎯 Fetching target timings for scan:', id);
    return api.get(`/scans/${id}/target-timings`);
  },
  exportTimingStatistics: (id: number) => {
    console.log('📤 Exporting timing statistics for scan:', id);
    return api.get(`/scans/${id}/timing-statistics/export`, { responseType: 'blob' });
  },

  // Enhanced Scan Statistics
  getEnhancedStatistics: (scanId: number) => {
    console.log('📊 Fetching enhanced statistics for scan:', scanId);
    return api.get(`/scans/${scanId}/enhanced-statistics`);
  },
  getEnhancedStatisticsTargets: (scanId: number) => {
    console.log('🎯 Fetching targets drill-down for scan:', scanId);
    return api.get(`/scans/${scanId}/enhanced-statistics/targets`);
  },
  getEnhancedStatisticsTargetPorts: (scanId: number, targetIP: string) => {
    console.log('🔌 Fetching ports for target:', targetIP, 'scan:', scanId);
    return api.get(`/scans/${scanId}/enhanced-statistics/targets/${encodeURIComponent(targetIP)}/ports`);
  },
  getEnhancedStatisticsPortTemplates: (
    scanId: number,
    targetIP: string,
    port: number,
    options?: { page?: number; pageSize?: number; severity?: string; status?: string; resultFound?: string }
  ) => {
    console.log('📋 Fetching templates for port:', port, 'target:', targetIP, 'scan:', scanId);
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('page_size', options.pageSize.toString());
    if (options?.severity) params.append('severity', options.severity);
    if (options?.status) params.append('status', options.status);
    if (options?.resultFound) params.append('result_found', options.resultFound);
    const queryString = params.toString();
    return api.get(`/scans/${scanId}/enhanced-statistics/targets/${encodeURIComponent(targetIP)}/ports/${port}/templates${queryString ? '?' + queryString : ''}`);
  },
  getEnhancedStatisticsTechnologies: (scanId: number) => {
    console.log('🔧 Fetching technologies drill-down for scan:', scanId);
    return api.get(`/scans/${scanId}/enhanced-statistics/technologies`);
  },
  getEnhancedStatisticsTechnologyTemplates: (
    scanId: number,
    technology: string,
    options?: { page?: number; pageSize?: number }
  ) => {
    console.log('📋 Fetching templates for technology:', technology, 'scan:', scanId);
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('page_size', options.pageSize.toString());
    const queryString = params.toString();
    return api.get(`/scans/${scanId}/enhanced-statistics/technologies/${encodeURIComponent(technology)}/templates${queryString ? '?' + queryString : ''}`);
  },
  getEnhancedStatisticsSelectionReasons: (scanId: number) => {
    console.log('🔍 Fetching selection reason breakdown for scan:', scanId);
    return api.get(`/scans/${scanId}/enhanced-statistics/selection-reasons`);
  },
  getEnhancedStatisticsTemplateExecutions: (
    scanId: number,
    options?: {
      page?: number;
      pageSize?: number;
      severity?: string;
      technology?: string;
      host?: string;
      port?: number;
      templateId?: string;
      status?: string;
      selectionReason?: string;
      resultFound?: string;
      search?: string;
    }
  ) => {
    console.log('📋 Fetching template executions for scan:', scanId);
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('page_size', options.pageSize.toString());
    if (options?.severity) params.append('severity', options.severity);
    if (options?.technology) params.append('technology', options.technology);
    if (options?.host) params.append('host', options.host);
    if (options?.port) params.append('port', options.port.toString());
    if (options?.templateId) params.append('template_id', options.templateId);
    if (options?.status) params.append('status', options.status);
    if (options?.selectionReason) params.append('selection_reason', options.selectionReason);
    if (options?.resultFound) params.append('result_found', options.resultFound);
    if (options?.search) params.append('search', options.search);
    const queryString = params.toString();
    return api.get(`/scans/${scanId}/enhanced-statistics/template-executions${queryString ? '?' + queryString : ''}`);
  },
  refreshEnhancedStatistics: (scanId: number) => {
    console.log('🔄 Refreshing enhanced statistics for scan:', scanId);
    return api.post(`/scans/${scanId}/enhanced-statistics/refresh`);
  },

  // Target summary for enhanced target table
  getTargetsSummary: (
    scanId: number,
    page: number = 1,
    perPage: number = 50,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    filters?: any
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...(sortBy && { sort_by: sortBy }),
      ...(sortOrder && { sort_order: sortOrder })
    });

    // CRITICAL FIX: Handle filters more carefully to prevent parameter issues
    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'search' && typeof value === 'string' && value.trim()) {
            params.append('search', value.toString().trim());
          } else if (key !== 'search') {
            params.append(key, (value as any).toString());
          }
        }
      });
    }

    const url = `/scans/${scanId}/targets-summary?${params}`;
    console.log('📊 Fetching targets summary for scan:', scanId, 'with params:', params.toString());
    console.log('📊 Full URL:', url);

    return api.get(url).catch(error => {
      console.error('❌ getTargetsSummary API error:', {
        scanId, url, params: params.toString(),
        error: error.message, response: error.response?.data, status: error.response?.status
      });
      throw error;
    });
  },

  // Scan Comparison
  compareScans: (scanIds: number[]) => {
    console.log('📊 Comparing scans:', scanIds);
    return api.post('/scans/compare', { scan_ids: scanIds });
  },

  // Exact side-by-side pair comparison (newer vs older) for the two-column diff view.
  compareScanPair: (scanIds: number[], includeInfo = false) => {
    const q = new URLSearchParams();
    q.set('ids', scanIds.join(','));
    if (includeInfo) q.set('include_info', 'true');
    return api.get(`/scans/compare/pair?${q.toString()}`);
  },

  // AI-powered comparison narrative for a scan pair. Omit compareTo to use the
  // auto-detected previous scan; provide it to compare against any chosen scan.
  getAIComparison: (scanId: number, compareTo?: number) => {
    const q = compareTo ? `?compare_to=${compareTo}` : '';
    return api.get(`/scans/${scanId}/ai/comparison${q}`);
  },
  generateAIComparison: (scanId: number, compareTo?: number, opts?: { force?: boolean; async?: boolean }) => {
    const params = new URLSearchParams();
    if (compareTo) params.set('compare_to', String(compareTo));
    if (opts?.force) params.set('force', 'true');
    if (opts?.async) params.set('async', 'true');
    const qs = params.toString();
    return api.post(`/scans/${scanId}/ai/comparison${qs ? '?' + qs : ''}`);
  },

  // Get top critical and high severity issues
  getTopIssues: () => {
    console.log('🚨 Fetching top critical and high severity issues');
    return api.get('/scans/top-issues');
  },

  // Scan Profiles (saved configurations)
  getProfiles: () => {
    return api.get('/scan-profiles');
  },
  getProfile: (id: number) => {
    return api.get(`/scan-profiles/${id}`);
  },
  createProfile: (data: { name: string; description: string; config: string }) => {
    return api.post('/scan-profiles', data);
  },
  updateProfile: (id: number, data: { name: string; description: string; config: string }) => {
    return api.put(`/scan-profiles/${id}`, data);
  },
  deleteProfile: (id: number) => {
    return api.delete(`/scan-profiles/${id}`);
  },

  // Attack Path Visualization
  getAttackPaths: (scanId: number, options?: { includeFalsePositives?: boolean; format?: 'json' | 'dot' }) => {
    console.log('🔀 Fetching attack paths for scan:', scanId);
    const params = new URLSearchParams();
    if (options?.includeFalsePositives) params.append('include_false_positives', 'true');
    if (options?.format) params.append('format', options.format);
    const queryString = params.toString();
    return api.get(`/scans/${scanId}/attack-paths${queryString ? '?' + queryString : ''}`);
  },

  // Intelligent Priority Scores
  getPriorityScores: (scanId: number, options?: { limit?: number; includeFalsePositives?: boolean; config?: any }) => {
    console.log('⚡ Fetching priority scores for scan:', scanId);
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.includeFalsePositives) params.append('include_false_positives', 'true');
    if (options?.config) params.append('config', JSON.stringify(options.config));
    const queryString = params.toString();
    return api.get(`/scans/${scanId}/priority-scores${queryString ? '?' + queryString : ''}`);
  },

  // Priority Summary
  getPrioritySummary: (scanId: number) => {
    console.log('📊 Fetching priority summary for scan:', scanId);
    return api.get(`/scans/${scanId}/priority-summary`);
  },

  // Executive Summary Report (Board-Ready One-Pager)
  exportExecutiveReport: (scanId: number, theme: 'dark' | 'bright' = 'dark', language?: string) => {
    console.log('📊 Exporting executive summary report for scan:', scanId, 'theme:', theme);
    const params = new URLSearchParams();
    params.append('theme', theme);
    if (language) params.append('language', language);
    return api.get(`/scans/${scanId}/export/executive-report?${params}`, {
      responseType: 'blob',
      timeout: 60000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
};
